import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../context/NotificationContext";
import { useUserRole } from "../context/UserRoleContext";
import { isNotificationGroupActionRequired } from "../utils/notificationKinds";

/**
 * Shared open flow for notification bell + full notifications page (modals + navigate).
 */
export function useNotificationNavigation(options = {}) {
  const { refreshShows, refreshArtists } = options;
  const { markAsRead, markGroupAsRead, fetchNotifications } = useNotifications();
  const { userData } = useUserRole();
  const navigate = useNavigate();

  const [selectedInviteId, setSelectedInviteId] = useState(null);
  const [selectedInviteNotificationId, setSelectedInviteNotificationId] =
    useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedShowId, setSelectedShowId] = useState(null);
  const [selectedRecurringGroupId, setSelectedRecurringGroupId] =
    useState(null);
  const [showShowModal, setShowShowModal] = useState(false);
  const [selectedBarOwnerGroupId, setSelectedBarOwnerGroupId] = useState(null);
  const [showBarOwnerModal, setShowBarOwnerModal] = useState(false);

  const handleNotificationClick = useCallback(
    (notificationGroup) => {
      const notification = notificationGroup.latestNotification;
      const role = userData?.role;
      const actionable = isNotificationGroupActionRequired(
        notificationGroup,
        role,
      );

      if (
        !actionable &&
        notificationGroup.unreadCount > 0
      ) {
        if (
          notificationGroup.isMessageGroup &&
          notificationGroup.notifications.length > 1
        ) {
          markGroupAsRead(notificationGroup);
        } else {
          markAsRead(notification.notification_id);
        }
      }

      if (
        notification.type === "invite_received" &&
        notification.related_entity_id
      ) {
        setSelectedInviteId(notification.related_entity_id);
        setSelectedInviteNotificationId(notification.notification_id ?? null);
        setShowInviteModal(true);
        return;
      }

      if (
        notification.type === "show_pending" &&
        notification.related_entity_id
      ) {
        setSelectedShowId(null);
        setSelectedRecurringGroupId(notification.related_entity_id);
        setShowShowModal(true);
        return;
      }

      if (
        (notification.type === "show_accepted" ||
          notification.type === "show_declined") &&
        notification.related_entity_id
      ) {
        setSelectedBarOwnerGroupId(notification.related_entity_id);
        setShowBarOwnerModal(true);
        return;
      }

      if (notification.action_url) {
        const basePath =
          userData?.role === "artist"
            ? "/artist"
            : userData?.role === "bar_owner"
              ? "/bar-owner"
              : "/bar-admin";

        navigate(`${basePath}${notification.action_url}`);
      }
    },
    [markAsRead, markGroupAsRead, navigate, userData?.role],
  );

  const handleInviteModalClose = useCallback(() => {
    setShowInviteModal(false);
    setSelectedInviteId(null);
    setSelectedInviteNotificationId(null);
    if (fetchNotifications) fetchNotifications();
  }, [fetchNotifications]);

  const handleInviteSuccess = useCallback(
    async (result, action) => {
      if (fetchNotifications) fetchNotifications();

      if (action === "accept" && userData?.role === "artist" && refreshArtists) {
        try {
          await refreshArtists();
        } catch (e) {
          /* optional context */
        }
        window.dispatchEvent(new CustomEvent("artistMembersUpdated"));
      }
    },
    [fetchNotifications, refreshArtists, userData?.role],
  );

  const handleShowModalClose = useCallback(() => {
    setShowShowModal(false);
    setSelectedShowId(null);
    setSelectedRecurringGroupId(null);
    if (fetchNotifications) fetchNotifications();
  }, [fetchNotifications]);

  const handleShowSuccess = useCallback(
    async () => {
      if (fetchNotifications) fetchNotifications();
      if (userData?.role === "artist" && refreshShows) {
        await refreshShows();
      }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("showsUpdated"));
      }, 100);
    },
    [fetchNotifications, refreshShows, userData?.role],
  );

  const handleBarOwnerModalClose = useCallback(() => {
    setShowBarOwnerModal(false);
    setSelectedBarOwnerGroupId(null);
    if (fetchNotifications) fetchNotifications();
  }, [fetchNotifications]);

  return {
    handleNotificationClick,
    inviteModal: {
      isOpen: showInviteModal,
      inviteId: selectedInviteId,
      notificationId: selectedInviteNotificationId,
      onClose: handleInviteModalClose,
      onSuccess: handleInviteSuccess,
    },
    showModal: {
      isOpen: showShowModal,
      showId: selectedShowId,
      recurringGroupId: selectedRecurringGroupId,
      onClose: handleShowModalClose,
      onSuccess: handleShowSuccess,
    },
    barOwnerModal: {
      isOpen: showBarOwnerModal,
      recurringGroupId: selectedBarOwnerGroupId,
      onClose: handleBarOwnerModalClose,
    },
  };
}
